"use client";
import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Code, Laptop, Server, Database, ShoppingCart, Wrench, Code2, Cloud } from "lucide-react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { FaUserTie, FaUserAlt } from "react-icons/fa";
import TechStackAccordionForWeb from "./TechStackAccordionForWeb";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../Components/ThemeContext";
import SEOHead from "../../Components/SEOHead";

// ─── JSON-LD Schema ───────────────────────────────────────────────────────────
const webSchema = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "Web Development",
  "provider": { "@type": "Organization", "name": "Fiieve", "url": "https://fiieve.com" },
  "description": "Fiieve builds high-performance, SEO-optimised websites and web applications using React, Next.js, Node.js, and modern CMS platforms. From landing pages to full-stack SaaS products.",
  "areaServed": "GB",
  "serviceType": "Web Development",
  "url": "https://fiieve.com/web-development",
  "offers": {
    "@type": "Offer",
    "description": "Custom web development using React.js, Next.js, Node.js, Tailwind CSS, WordPress, and cloud hosting on AWS, Vercel, and Netlify."
  },
  "breadcrumb": {
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home",            "item": "https://fiieve.com/" },
      { "@type": "ListItem", "position": 2, "name": "Services",        "item": "https://fiieve.com/services" },
      { "@type": "ListItem", "position": 3, "name": "Web Development", "item": "https://fiieve.com/web-development" }
    ]
  }
});

const lightGridStyle = {
  backgroundImage: `linear-gradient(rgba(74,222,128,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(74,222,128,0.07) 1px, transparent 1px)`,
  backgroundSize: "40px 40px",
};
const darkGridStyle = {
  backgroundImage: `linear-gradient(rgba(74,222,128,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(251,146,60,0.05) 1px, transparent 1px)`,
  backgroundSize: "40px 40px",
};

const fadeUp = {
  hidden: { opacity: 0, y: 50 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

function CountUp({ target, duration = 1200 }) {
  const [display, setDisplay] = useState("0");
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  useEffect(() => {
    if (!inView) return;
    if (target.includes("–") || target.includes("/")) {
      const t = setTimeout(() => setDisplay(target), 400);
      return () => clearTimeout(t);
    }
    const numeric = parseFloat(target.replace(/[^\d.]/g, ""));
    const suffix  = target.replace(/[\d.]/g, "");
    const isDecimal = target.includes(".");
    let start = 0;
    const inc = numeric / (duration / 16);
    const timer = setInterval(() => {
      start += inc;
      if (start >= numeric) {
        setDisplay(`${isDecimal ? numeric.toFixed(1) : numeric}${suffix}`);
        clearInterval(timer);
      } else {
        setDisplay(`${isDecimal ? start.toFixed(1) : Math.floor(start)}${suffix}`);
      }
    }, 16);
    return () => clearInterval(timer);
  }, [inView, target, duration]);
  return <span ref={ref}>{display}</span>;
}

function ServiceCard({ icon: Icon, title, description, isDark }) {
  return (
    <motion.div
      variants={fadeUp}
      whileHover={{ scale: 1.05 }}
      transition={{ type: "spring", stiffness: 200, damping: 12 }}
      className={`${isDark ? "bg-gray-800" : "bg-[#1a2e1a]"} border-2 border-green-500 rounded-lg p-6 hover:border-green-400 transition-all duration-300 h-full`}
    >
      <div className="flex items-start gap-4 w-full">
        <div className="bg-green-500 p-2.5 rounded-lg flex-shrink-0 self-start">
          <Icon className="w-6 h-6 text-white" />
        </div>
        <div>
          <h3 className="text-green-400 text-base font-bold mb-2 leading-tight">{title}</h3>
          <p className="text-gray-300 text-xs leading-relaxed">{description}</p>
        </div>
      </div>
    </motion.div>
  );
}

export default function WebDevelopmentPage() {
  const { isDark } = useTheme();
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const navigate = useNavigate();

  const greenHeading = isDark ? "text-green-400" : "text-green-700";
  const headingText  = isDark ? "text-white"     : "text-gray-900";
  const bodyText     = isDark ? "text-gray-400"  : "text-gray-600";
  const statCardBg   = isDark ? "bg-gray-800/80" : "bg-[#1a2e1a]";
  const testimCardBg = isDark ? "bg-[#0a0a0a]"  : "bg-[#1a2e1a]";
  const ctaBoxBg     = isDark ? "bg-black"       : "bg-[#1a2e1a]";
  const techCardBg   = isDark ? "bg-black"       : "bg-[#1a2e1a]";

  // ✅ SEO: service descriptions use SRS keywords
  const services = [
    { icon: Code,         title: "Custom Web Development",  description: "Custom, scalable websites and web apps built with React and Next.js for performance, SEO, and long-term reliability." },
    { icon: Laptop,       title: "Frontend Engineering",    description: "Pixel-perfect, responsive UIs built with React, Next.js, and Tailwind CSS — fast-loading and accessible across all devices." },
    { icon: Server,       title: "Backend Development",     description: "Secure, robust backend systems and REST APIs built with Node.js and Express.js to power modern, data-rich applications." },
    { icon: Database,     title: "CMS Development",         description: "Easy-to-manage websites using WordPress, Webflow, or fully custom CMS solutions tailored to your content workflow." },
    { icon: ShoppingCart, title: "E-Commerce Development",  description: "Secure, high-converting online stores with seamless Stripe, PayPal, and third-party payment gateway integration." },
    { icon: Wrench,       title: "Maintenance & Support",   description: "Continuous updates, security patches, performance monitoring, and iterative improvements for long-term digital success." },
  ];

  const serviceCards = [
    { number: "98%", label: "Client Satisfaction",    icon: "/Images/satisfaction.png" },
    { number: "40%", label: "Average ROI Increase",   icon: "/Images/roi.png" },
    { number: "3–6", label: "Weeks Delivery Time",    icon: "/Images/time.png" },
    { number: "50+", label: "Award-Winning Projects", icon: "/Images/award.png" },
  ];

  const testimonials = [
    { id: 1, name: "Ahmad Raza",   handle: "@ahmadraza_dev",   gender: "male",   title: "Outstanding Web Development",   feedback: "Fiieve delivered our e-commerce platform on time and beyond our expectations. Clean code, proactive communication, and our sales have grown 40% since launch." },
    { id: 2, name: "Sana Mirza",   handle: "@sanamirza_ux",    gender: "female", title: "Brilliant Frontend Work",        feedback: "The frontend team at Fiieve completely transformed our web app's interface. They delivered a clean, intuitive experience and our user retention improved significantly." },
    { id: 3, name: "Bilal Tariq",  handle: "@bilaltariq_ceo",  gender: "male",   title: "Reliable Backend Systems",      feedback: "We needed a robust backend for our platform and Fiieve delivered exactly that. Scalable APIs, secure architecture, and excellent communication throughout the project." },
    { id: 4, name: "Nadia Hassan", handle: "@nadiahassan_biz", gender: "female", title: "Exceptional Support & Delivery", feedback: "Fiieve built our company website from scratch and the results were incredible. They provided regular updates and were always available to address our feedback promptly." },
  ];

  const techCategories = [
    { icon: Code2,    title: "Frontend",            description: "Beautiful, responsive interfaces that users love",         technologies: ["React.js", "Next.js", "HTML5", "CSS3", "Tailwind CSS"],  benefits: ["Fast-loading pages", "Mobile-friendly designs", "Smooth animations", "Accessible interfaces"] },
    { icon: Server,   title: "Backend",             description: "Powerful systems that handle your business logic",         technologies: ["Node.js", "Express.js", "MongoDB", "Firebase"],          benefits: ["Secure REST APIs", "Scalable architecture", "Optimised performance", "Reliable data handling"] },
    { icon: Database, title: "CMS & Platforms",     description: "Easy-to-manage platforms and CMS solutions",              technologies: ["WordPress", "Webflow", "Framer"],                         benefits: ["Quick content updates", "Customisable templates", "User-friendly dashboards", "Flexible integrations"] },
    { icon: Cloud,    title: "Hosting & Deployment",description: "Reliable, scalable cloud hosting for your applications",  technologies: ["AWS", "Vercel", "Netlify"],                               benefits: ["High availability", "Automatic scaling", "Secure infrastructure", "Fast deployment"] },
  ];

  // ✅ SEO: tech tags from SRS 3.2
  const techTags = ["React.js", "Next.js", "Node.js", "Tailwind CSS", "WordPress", "REST APIs", "AWS"];

  const sliderSettings = {
    dots: true, infinite: false, speed: 800, slidesToShow: 2, slidesToScroll: 1, autoplay: false,
    responsive: [{ breakpoint: 768, settings: { slidesToShow: 1, slidesToScroll: 1 } }],
  };

  return (
    <>
      {/* ✅ SEO: title, meta description, canonical URL, JSON-LD */}
      <SEOHead
        title="Web Development Services UK | React, Next.js & Node.js | Fiieve"
        description="Fiieve builds high-performance websites and web apps using React, Next.js, Node.js, and Tailwind CSS. E-commerce, CMS, SaaS, and full-stack web development for UK startups and enterprises."
        canonical="https://fiieve.com/web-development"
        ogImage="https://fiieve.com/Images/og-web.jpg"
        schema={webSchema}
      />

      <main
        className={`relative overflow-hidden pt-20 sm:pt-24 lg:pt-28 transition-colors duration-300 ${isDark ? "bg-black text-white" : "bg-[#f0f4f0] text-gray-900"}`}
        style={isDark ? darkGridStyle : lightGridStyle}
      >
        {!isDark && (
          <div className="pointer-events-none fixed inset-0 overflow-hidden" style={{ zIndex: 0 }}>
            <div style={{ position:"absolute", top:"-10%", left:"-5%",  width:"55vw", height:"55vw", background:"radial-gradient(circle, rgba(74,222,128,0.13) 0%, transparent 70%)", filter:"blur(40px)" }} />
            <div style={{ position:"absolute", top:"-8%",  right:"-8%", width:"50vw", height:"50vw", background:"radial-gradient(circle, rgba(251,146,60,0.11) 0%, transparent 70%)",  filter:"blur(40px)" }} />
            <div style={{ position:"absolute", top:"45%",  left:"20%",  width:"60vw", height:"40vw", background:"radial-gradient(ellipse, rgba(74,222,128,0.07) 0%, transparent 65%)", filter:"blur(55px)" }} />
          </div>
        )}

        <div className="relative" style={{ zIndex: 1 }}>

          {/* ── HERO BANNER ── */}
          <div className="relative h-[25vh] overflow-hidden" style={{ marginBottom: "-3px" }}>
            <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('/Images/bg2.png')" }} aria-hidden="true" />
            <div className="absolute inset-0 bg-black/80" aria-hidden="true" />
            <div className="relative mx-auto max-w-7xl px-6 md:px-8 h-full flex flex-col items-center justify-center text-center">
              {/* ✅ SEO: H1 unchanged */}
              <h1 className="text-3xl md:text-5xl font-extrabold text-green-400">
                Web Development
              </h1>
              {/* ✅ SEO: semantic breadcrumb */}
              <nav aria-label="Breadcrumb" className="mt-3">
                <ol className="flex items-center justify-center gap-1 text-sm text-white" role="list">
                  <li><a href="/" className="hover:text-green-400 transition-colors">Home</a></li>
                  <li aria-hidden="true" className="text-gray-400">/</li>
                  <li><a href="/services" className="hover:text-green-400 transition-colors">Services</a></li>
                  <li aria-hidden="true" className="text-gray-400">/</li>
                  <li aria-current="page" className="text-green-400">Web Development</li>
                </ol>
              </nav>
            </div>
          </div>

          {/* ── INTRO — SRS copy + tech tag pills ── */}
          <motion.section
            aria-labelledby="web-intro-heading"
            className="pt-10 pb-4 px-6 max-w-7xl mx-auto text-center"
            variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
          >
            <p className={`text-sm font-semibold uppercase tracking-widest ${greenHeading} mb-2`}>
              React • Next.js • Node.js • Full-Stack
            </p>
            <h2
              id="web-intro-heading"
              className={`text-2xl sm:text-3xl md:text-4xl font-bold ${headingText} mb-5`}
            >
              From Landing Pages to Full-Stack SaaS —{" "}
              <span className={greenHeading}>Web Development Built for Performance & Growth</span>
            </h2>
            {/* ✅ SEO: keyword-rich SRS description */}
            <p className={`${bodyText} text-lg md:text-xl leading-relaxed mb-8`}>
              Whether you need a marketing website, a complex web application, or a scalable SaaS platform, Fiieve delivers
              web development that combines stunning design with rock-solid engineering. We use{" "}
              <strong className={headingText}>React.js</strong> and{" "}
              <strong className={headingText}>Next.js</strong> for lightning-fast frontends,{" "}
              <strong className={headingText}>Node.js</strong> for secure and scalable backends, and{" "}
              <strong className={headingText}>Tailwind CSS</strong> for pixel-perfect, responsive interfaces.
              From MVP to full production deployment, we manage the complete web development lifecycle.
            </p>
            {/* ✅ SEO: tech tag pills — same style as mobile page */}
            <div
              className="inline-flex flex-wrap justify-center items-center gap-1 p-1.5 rounded-full"
              style={{ background: isDark ? "#111" : "#0f1f0f", border: "1px solid rgba(74,222,128,0.25)" }}
              aria-label="Technologies used in web development"
            >
              {techTags.map((tag) => (
                <span
                  key={tag}
                  className="px-4 py-1.5 rounded-full text-base font-semibold text-white/80 hover:text-white hover:bg-white/10 transition-colors duration-200 cursor-default"
                >
                  {tag}
                </span>
              ))}
            </div>
          </motion.section>

          {/* ── SERVICES ── */}
          <section aria-labelledby="services-heading" className="pt-8 pb-12 px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-8">
                <h2 className={`text-xl sm:text-2xl font-semibold ${headingText} mb-1`}>What We Build</h2>
                <h2 id="services-heading" className={`${greenHeading} text-3xl md:text-4xl lg:text-5xl font-bold`}>
                  Our Web Development Services
                </h2>
              </div>
              <motion.div
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch"
                initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
              >
                {services.map((s, i) => (
                  <ServiceCard key={i} icon={s.icon} title={s.title} description={s.description} isDark={isDark} />
                ))}
              </motion.div>
            </div>
          </section>

          {/* ── STATS ── */}
          <section aria-labelledby="stats-heading" className="pb-16">
            <div className="max-w-7xl mx-auto px-6 text-center">
              <motion.div className="mb-10" variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}>
                <h2 className={`text-xl sm:text-2xl font-semibold ${headingText} mb-2`}>Our Track Record</h2>
                <h2 id="stats-heading" className={`${greenHeading} text-3xl md:text-4xl lg:text-5xl font-bold`}>
                  Real Results for Real Businesses
                </h2>
              </motion.div>
              <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10" variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}>
                {serviceCards.map((card, i) => (
                  <motion.div
                    key={i}
                    whileHover={{ scale: 1.08, boxShadow: "0 0 30px rgba(34,197,94,0.6)" }}
                    transition={{ type: "spring", stiffness: 200, damping: 12 }}
                    className={`border border-green-500/50 rounded-2xl p-8 text-center hover:border-green-500 transition-all duration-300 ${statCardBg} flex flex-col items-center`}
                  >
                    <img src={card.icon} alt={card.label} className="w-20 h-20 mb-4 object-contain" loading="lazy" width={80} height={80} />
                    <div className="text-4xl font-extrabold text-green-500 mb-2"><CountUp target={card.number} /></div>
                    <p className="text-gray-300 text-lg font-semibold">{card.label}</p>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </section>

          {/* ── TESTIMONIALS ── */}
          <section aria-labelledby="testimonials-heading">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-6xl mx-auto px-4 mt-4 mb-12">
              <h2 className={`text-xl sm:text-2xl font-semibold ${headingText} text-center mb-2`}>What Our Clients Say</h2>
              <h2 id="testimonials-heading" className={`${greenHeading} text-3xl md:text-4xl font-bold text-center mb-8 md:mb-12`}>
                Client Feedback
              </h2>
              {!isDark && <style>{`.slick-prev:before, .slick-next:before { color: #166534 !important; }`}</style>}
              <Slider {...sliderSettings}>
                {testimonials.map((t) => (
                  <div key={t.id} className="px-3">
                    <motion.article
                      initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
                      aria-label={`Testimonial from ${t.name}`}
                      className={`${testimCardBg} border border-green-400/30 rounded-2xl p-6 md:p-8 shadow-lg hover:shadow-green-400/10 hover:border-green-400/60 transition-all duration-300 flex flex-col sm:flex-row items-center sm:items-start gap-5`}
                    >
                      <div className="w-14 h-14 md:w-16 md:h-16 rounded-full border-2 border-green-400/40 bg-[#0d1a0f] flex items-center justify-center flex-shrink-0" aria-hidden="true">
                        {t.gender === "male" ? <FaUserTie size={28} className="text-green-400" /> : <FaUserAlt size={24} className="text-green-300" />}
                      </div>
                      <div className="text-center sm:text-left flex flex-col gap-2">
                        <h3 className="text-lg font-bold text-white">{t.title}</h3>
                        <p className="text-gray-400 text-sm leading-relaxed">{t.feedback}</p>
                        <div className="flex items-center justify-center sm:justify-start gap-0.5 mt-1" aria-label="5 star rating">
                          {Array(5).fill().map((_, i) => <span key={i} className="text-green-400 text-lg" aria-hidden="true">★</span>)}
                        </div>
                        <p className="text-green-400 font-semibold text-sm">{t.name}</p>
                        <p className="text-gray-500 text-xs">{t.handle}</p>
                      </div>
                    </motion.article>
                  </div>
                ))}
              </Slider>
            </motion.div>
          </section>

          {/* ── CTA ── */}
          <motion.section
            aria-label="Start your web development project"
            initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
            className="max-w-5xl mx-auto px-4 mb-16"
          >
            <div className={`${ctaBoxBg} border-2 border-green-500 rounded-2xl p-10 flex flex-col items-center gap-6 text-center`}>
              <div className="w-full max-w-3xl">
                <h2 className="text-xl md:text-2xl font-bold text-white mb-3">
                  Ready to Build Your Web Application?
                </h2>
                <p className="text-gray-300 text-base md:text-lg leading-relaxed">
                  Whether you need a marketing website, an e-commerce store, or a full-stack web application — our web development team delivers high-performance, SEO-optimised results. Get in touch today.
                </p>
              </div>
              <button
                onClick={() => navigate("/contact-us")}
                aria-label="Contact Fiieve to start your web development project"
                className="bg-green-500 hover:bg-green-600 text-black font-bold py-3 px-6 rounded-lg transition-all duration-300"
              >
                Discuss Your Web Project
              </button>
            </div>
          </motion.section>

          {/* ── TECHNOLOGIES ── */}
          <section aria-labelledby="tech-heading" className="py-6 md:py-8 px-4 -mt-12 md:-mt-16">
            <div className="max-w-7xl mx-auto">
              <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="text-center mb-10">
                <h2 className={`text-xl sm:text-2xl font-semibold ${headingText} mb-2`}>Our Stack</h2>
                <h2 id="tech-heading" className={`${greenHeading} text-3xl md:text-4xl lg:text-5xl font-bold mb-4`}>
                  Technologies We Use
                </h2>
                <p className={`${bodyText} text-lg max-w-2xl mx-auto`}>
                  We leverage cutting-edge technologies — React, Next.js, Node.js, and cloud platforms — to build scalable, modern web solutions.
                </p>
              </motion.div>
              <motion.div
                className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8"
                initial="hidden" whileInView="show" viewport={{ once: true }}
                variants={{ show: { transition: { staggerChildren: 0.15 } } }}
              >
                {techCategories.map((category, index) => {
                  const Icon = category.icon;
                  return (
                    <motion.article
                      key={index}
                      aria-label={`${category.title} technology stack`}
                      className={`${techCardBg} border-2 border-green-500 rounded-2xl p-6 md:p-8 transition-all duration-300 flex flex-col w-full min-h-[220px]`}
                      whileHover={{ scale: 1.05, boxShadow: "0 0 25px 5px rgba(34,197,94,0.7)", borderColor: "#22C55E" }}
                      transition={{ type: "spring", stiffness: 200, damping: 12 }}
                    >
                      <div className="flex items-center gap-4 mb-4">
                        <div className="bg-green-500 p-3 rounded-lg flex-shrink-0"><Icon className="w-8 h-8 text-black" /></div>
                        <div>
                          <h3 className="text-green-400 text-xl md:text-2xl font-bold">{category.title}</h3>
                          <p className="text-gray-400 text-sm md:text-base">{category.description}</p>
                        </div>
                      </div>
                      <div className="mb-4">
                        <p className="text-gray-300 font-semibold text-sm mb-1">Primary Technologies:</p>
                        <p className="text-gray-300">{category.technologies.join(", ")}</p>
                      </div>
                      <div>
                        <p className="text-gray-300 font-semibold text-sm mb-1">Benefits:</p>
                        <ul className="list-disc list-inside text-gray-300 space-y-1">
                          {category.benefits.map((b, i) => <li key={i}>{b}</li>)}
                        </ul>
                      </div>
                    </motion.article>
                  );
                })}
              </motion.div>
            </div>
          </section>

          <div className="py-12 px-4">
            <TechStackAccordionForWeb />
          </div>

        </div>
      </main>
    </>
  );
}